import { CartSalePrice } from '@cart-ui/CartSalePrice/CartSalePrice'
import { FAST_ORDER_ITEM_CHECK_STATUS } from '@hooks-api/types/dataModel/fast-order'
import { Checkbox, type CheckboxProps } from '@pets-ui/Checkbox/Checkbox'
import { Chip } from '@pets-ui/Chip/Chip'
import { HStack } from '@pets-ui/HStack/HStack'
import { NumberInput } from '@pets-ui/NumberInput/NumberInput'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { type MouseEventHandler } from 'react'
import { ButlerItemImage } from '../../../../fp-b2b-shared/ai-purchase-order/components/ButlerListItem/ButlerItemImage'
import { ButlerListItemStock } from '../../../../fp-b2b-shared/ai-purchase-order/components/ButlerListItem/ButlerListItemStock'
import { IconButtonDeleteItem } from '../../../../fp-b2b-shared/ai-purchase-order/components/ButlerListItem/IconButtonDeleteItem'
import { type CsvButlerItem, isCsvItemBlocked, isCsvItemPending, isCsvItemQuantityInvalid, MIN_CSV_ITEM_QUANTITY } from '../../../hooks/use-stm-quick-order-store'
import { formatCsvItemTitle } from './format-csv-item-title'

const MAX_QUANTITY = 99999

const styles = stylex.create({
  numberInputButton: {
    height: '24px',
    width: '24px',
  },
  numberInput: {
    backgroundColor: colors.white,
    width: '42px',
  },
  title: {
    maxWidth: '260px',
  },
  skuChip: {
    alignSelf: 'flex-start',
  },
})

export interface CsvButlerListItemProps {
  item: CsvButlerItem
  disabled?: boolean
  onQuantityChange: (value?: number) => void
  onCheckboxChange: CheckboxProps['onChange']
  onDelete: MouseEventHandler<HTMLButtonElement>
}

export const CsvButlerListItem = ({ item, disabled = false, onQuantityChange, onCheckboxChange, onDelete }: CsvButlerListItemProps) => {
  const isMobile = useResponsive('mobile')
  const isBlocked = isCsvItemBlocked(item)
  const isPending = isCsvItemPending(item)
  const isQuantityInvalid = isCsvItemQuantityInvalid(item)
  const checked = item.checked && !isBlocked
  const remainingQuantity = item.check?.remainingQuantity ?? null
  const unitPrice = item.check?.unitPrice ?? item.product.unitPrice
  const lineTotal = item.check?.amount ?? unitPrice * item.quantity
  const title = formatCsvItemTitle(item.product)
  const showsStockLine = !isPending && remainingQuantity !== null
  // 缺貨由庫存列的「(缺貨)」說明，其餘 blocked 狀態（或缺貨但拿不到剩餘數量）才顯示 errorMessage
  const stockLineExplainsBlock = showsStockLine && item.check?.status === FAST_ORDER_ITEM_CHECK_STATUS.OUT_OF_STOCK
  const blockedReason = isBlocked && !stockLineExplainsBlock ? item.check?.errorMessage : null

  const quantityInput = (
    <VStack alignItems="center" gap={1} width={120} flexShrink={0}>
      <NumberInput
        value={item.quantity}
        min={0}
        max={MAX_QUANTITY}
        onChange={onQuantityChange}
        autoSelect
        disabled={disabled}
        ButtonDecrementProps={{ rootStyle: styles.numberInputButton }}
        ButtonIncrementProps={{ rootStyle: styles.numberInputButton }}
        disabledIncrement={isBlocked || disabled}
        disabledDecrement={item.quantity <= MIN_CSV_ITEM_QUANTITY}
        error={isQuantityInvalid}
        rootStyle={styles.numberInput}
      />
      {isQuantityInvalid && <Text variant="caption" color={colors.red600}>數量需大於 0</Text>}
      {isPending && <Text variant="caption" color={colors.grey600}>檢查中</Text>}
      {showsStockLine && <ButlerListItemStock stock={remainingQuantity} quantity={item.quantity} />}
      {blockedReason && <Text variant="caption" color={colors.red600}>{blockedReason}</Text>}
    </VStack>
  )
  const skuChip = <Chip variant="square" rootStyle={styles.skuChip}>{item.realSku}</Chip>

  if (isMobile) {
    return (
      <HStack backgroundColor={isBlocked ? colors.red50 : colors.white} p={3} gap={2.5} borderRadius={8} alignItems="center" flexWrap="nowrap">
        <Checkbox color="blue" checked={checked} onChange={onCheckboxChange} disabled={isBlocked || disabled} />
        <ButlerItemImage productImgSrc={item.product.imageUrl ?? undefined} productTitle={title} productImgWidth={84} />
        <VStack flex={1} gap={2} overflow="hidden">
          <HStack justifyContent="space-between" alignItems="flex-start" flexWrap="nowrap" gap={3}>
            <VStack gap={2} overflow="hidden">
              <Text variant="subtitle" noWrap ellipsis>{title}</Text>
              {skuChip}
            </VStack>
            <IconButtonDeleteItem onClick={onDelete} disabled={disabled} />
          </HStack>
          <HStack justifyContent="space-between" alignItems="flex-end" flexWrap="nowrap">
            <HStack gap={1} alignItems="flex-end">
              <CartSalePrice salePrice={lineTotal} quantity={1} priceType="totalPrice" />
              <CartSalePrice salePrice={unitPrice} quantity={item.quantity} priceType="salePrice" />
            </HStack>
            {quantityInput}
          </HStack>
        </VStack>
      </HStack>
    )
  }

  return (
    <HStack backgroundColor={isBlocked ? colors.red50 : colors.white} p={3} gap={5} borderRadius={8} alignItems="center" flexWrap="nowrap">
      <Checkbox color="blue" checked={checked} onChange={onCheckboxChange} disabled={isBlocked || disabled} />
      <HStack gap={4} alignItems="center" flex={1} flexWrap="nowrap" overflow="hidden">
        <ButlerItemImage productImgSrc={item.product.imageUrl ?? undefined} productTitle={title} productImgWidth={68} />
        <VStack gap={1}>
          <Text variant="subtitle" lineClamp={2} rootStyle={styles.title}>{title}</Text>
          {skuChip}
        </VStack>
      </HStack>
      <HStack gap={10} alignItems="center" flexWrap="nowrap">
        <CartSalePrice salePrice={unitPrice} quantity={item.quantity} priceType="salePrice" />
        {quantityInput}
        <HStack minWidth={60} justifyContent="flex-end">
          <CartSalePrice salePrice={lineTotal} quantity={1} priceType="totalPrice" />
        </HStack>
      </HStack>
      <IconButtonDeleteItem onClick={onDelete} disabled={disabled} />
    </HStack>
  )
}
